import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import withStyles from 'react-jss';
import { Grid, Header } from 'semantic-ui-react';
import ReactTooltip from 'react-tooltip';
import { isEmpty } from 'ramda';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';

import { ClassProp } from 'helpers/types';
import Button from 'components/Button';
import DisplayTimeStamp from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/DisplayTimestamp';
import {
  DisplayLabels,
  DataByKey,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';
import { dateIsAfterCurrentDate, utcToCST } from 'helpers/date';
import {
  Segment,
  TargetingRuleType,
  Throttle,
  TargetingRule,
  SegmentAction,
  SegmentActionType,
  OfferSummary,
  Audience,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import UpdateThrottle from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/UpdateThrottle/components/UpdateThrottle';
import UpdateNameAndDescriptionThrottle from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/UpdateThrottle/components/UpdateNameAndDescriptionThrottle';
import PulseSegment from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/PulseSegment';
import ReplaceSegment from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/ReplaceSegment';
import TerminateSegment from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/TerminateSegment';
import ModifySegment from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/ModifySegment';
import { AttachedAudiences } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/SelectAudience/SelectAudience';
import ThrottleDetails from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/ThrottleDetails/ThrottleDetails';

import {
  renderDollarAmt,
  renderCashBackPercent,
  renderInlineField,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/OfferSegments/components/OfferSegment/OfferSegment.helpers';

import styles from './OfferSegment.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  segment: Segment;
  disablePulse: boolean;
  activeSegment: SegmentAction;
  setActiveSegment: React.Dispatch<React.SetStateAction<SegmentAction>>;
  attachedAudiences: AttachedAudiences;
  classes: Styles;
  expired: boolean;
  deleted: boolean;
  offer?: OfferSummary;
};

const renderCashBack = (
  {
    cashbackAmount,
    cashbackPercentage,
    cashbackDailyLimit,
    baseCashbackAmount = 0,
    baseCashbackPercentage = 0,
    promotionType,
  }: Segment,
  classes: Styles,
) => {
  const cashBack = cashbackAmount
    ? renderInlineField(
        'Cashback Amount:',
        renderDollarAmt(cashbackAmount),
        classes,
      )
    : renderInlineField(
        'Cashback Percent:',
        renderCashBackPercent(cashbackPercentage),
        classes,
      );
  const baseCashBack = baseCashbackAmount
    ? renderInlineField(
        'Base Amount:',
        renderDollarAmt(baseCashbackAmount),
        classes,
      )
    : renderInlineField(
        'Base Percent:',
        renderCashBackPercent(baseCashbackPercentage),
        classes,
      );

  return (
    <>
      {cashBack}
      {promotionType === 'CASH_FLASH' && baseCashBack}
      {renderInlineField(
        'Daily Limit:',
        renderDollarAmt(cashbackDailyLimit),
        classes,
      )}
    </>
  );
};

const renderUpdateThrottle = (
  offerUuid: string,
  setUpdateThrottle: React.Dispatch<React.SetStateAction<boolean>>,
  throttle: Throttle,
) => (
  <UpdateThrottle
    offerUuid={offerUuid}
    throttle={throttle}
    updateThrottle={setUpdateThrottle}
  />
);

const renderUpdateNameAndDescriptionThrottle = (
  offerUuid: string,
  setIsUpdateNameAndDescriptionFormOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  throttle: Throttle,
) => (
  <UpdateNameAndDescriptionThrottle
    offerUuid={offerUuid}
    throttle={throttle}
    isUpdateNameAndDescriptionFormOpen={setIsUpdateNameAndDescriptionFormOpen}
  />
);

const renderThrottleDetails = (
  handleClick: React.Dispatch<React.SetStateAction<boolean>>,
  handleEditNameAndDescriptionClick: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  classes: Styles,
  expired: boolean,
  throttle: Throttle,
) => (
  <ThrottleDetails
    throttle={throttle}
    handleClick={handleClick}
    handleEditNameAndDescriptionClick={handleEditNameAndDescriptionClick}
    classes={classes}
    disabled={expired}
  />
);

const renderThrottle = (
  offerUuid: string,
  updateThrottle: boolean,
  setUpdateThrottle: React.Dispatch<React.SetStateAction<boolean>>,
  isUpdateNameAndDescriptionFormOpen: boolean,
  setIsUpdateNameAndDescriptionFormOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  idx: number,
  classes: Styles,
  expired: boolean,
  throttle?: Throttle,
) => {
  const handleClick = () => {
    setUpdateThrottle(true);
  };

  const handleEditNameAndDescriptionClick = () => {
    setIsUpdateNameAndDescriptionFormOpen(true);
  };

  return (
    <div key={`rule_${idx}`} className={classes.container}>
      <Header className={classes.textLabel} as="h4">
        Throttle
      </Header>
      {throttle ? (
        updateThrottle && !expired ? (
          renderUpdateThrottle(offerUuid, setUpdateThrottle, throttle)
        ) : isUpdateNameAndDescriptionFormOpen ? (
          renderUpdateNameAndDescriptionThrottle(
            offerUuid,
            setIsUpdateNameAndDescriptionFormOpen,
            throttle,
          )
        ) : (
          renderThrottleDetails(
            handleClick,
            handleEditNameAndDescriptionClick,
            classes,
            expired,
            throttle,
          )
        )
      ) : (
        <p>This segment is not setup with a throttle</p>
      )}
    </div>
  );
};

const renderAudienceDetails = (
  { name, status, effectiveAt }: Audience,
  classes: Styles,
) => (
  <div>
    {renderInlineField('Name:', name, classes)}
    {renderInlineField('Status:', status, classes)}
    <DisplayTimeStamp label="Effective At:" date={utcToCST(effectiveAt)} />
  </div>
);

const renderAudience = (idx: number, classes: Styles, audience?: Audience) => (
  <div key={`rule_${idx}`} className={classes.container}>
    <Header className={classes.textLabel} as="h4">
      Audience
    </Header>
    {audience ? (
      renderAudienceDetails(audience, classes)
    ) : (
      <p>This segment is not setup with an Audience</p>
    )}
  </div>
);

const renderData = (data: string[]) => {
  return data.map((item, idx) => <li key={idx}> {item} </li>);
};

const renderDataByKey = (dataByKey: DataByKey) => {
  const keys = Object.keys(dataByKey);
  return keys.map((key: string) => {
    return renderData(dataByKey[key]);
  });
};

const renderTargetingRule = (
  rule: TargetingRule,
  idx: number,
  classes: Styles,
) => (
  <div key={`rule_${idx}`} className={classes.container}>
    <Header className={classes.textLabel} as="h4">
      {rule.type}
    </Header>
    <ul className={classes.listItems}>
      {isEmpty(rule.dataByKey)
        ? renderData(rule.data)
        : renderDataByKey(rule.dataByKey)}
    </ul>
  </div>
);

const renderTargetingRules = (
  offerUuid: string,
  { targetingRules, throttle, audience }: Segment,
  updateThrottle: boolean,
  setUpdateThrottle: React.Dispatch<React.SetStateAction<boolean>>,
  isUpdateNameAndDescriptionFormOpen: boolean,
  setIsUpdateNameAndDescriptionFormOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >,
  classes: Styles,
  expired: boolean,
) => (
  <>
    <Grid.Row className={classes.segmentHeaderRow}>
      <Header className={classes.header} as="h3">
        Targeting Rules:
      </Header>
    </Grid.Row>
    <Grid.Row className={classes.segmentDetailRow}>
      {targetingRules.map((rule, idx) => {
        switch (rule.type) {
          case TargetingRuleType.BASE:
          case TargetingRuleType.MUG:
            return renderTargetingRule(rule, idx, classes);
          case TargetingRuleType.AUDIENCE:
            return renderAudience(idx, classes, audience);
          case TargetingRuleType.OFFER_THROTTLE_V1:
            return renderThrottle(
              offerUuid,
              updateThrottle,
              setUpdateThrottle,
              isUpdateNameAndDescriptionFormOpen,
              setIsUpdateNameAndDescriptionFormOpen,
              idx,
              classes,
              expired,
              throttle,
            );
          default:
            break;
        }
      })}
    </Grid.Row>
  </>
);

const renderCashbackDetails = (
  segment: Segment,
  classes: Styles,
  offer?: OfferSummary,
) => (
  <>
    <Grid.Row className={classes.segmentHeaderRow}>
      <Header className={classes.header} as="h3">
        Cash Back Details:
      </Header>
    </Grid.Row>
    <Grid.Row className={classes.segmentDetailRow}>
      {renderCashBack(segment, classes)}
      {offer &&
        renderInlineField(
          'Min Spend:',
          renderDollarAmt(offer?.minimumSpendToActivate ?? '0'),
          classes,
        )}
      {offer?.exactSpendToActivate &&
        renderInlineField(
          'Exact Spend:',
          JSON.parse(
            offer?.exactSpendToActivate ?? '[]',
          ).map((spend: number) => (
            <span key={spend}>{renderDollarAmt(spend)} </span>
          )),
          classes,
        )}
    </Grid.Row>
  </>
);

const renderFinancialTerms = (segment: Segment, classes: Styles) => {
  const {
    financialTerm: { financialTermItems },
  } = segment;
  const { FinancialTermLabels } = DisplayLabels;
  return (
    <>
      <Grid.Row className={classes.segmentHeaderRow}>
        <Header className={classes.header} as="h3">
          Financial Terms:
        </Header>
      </Grid.Row>
      <Grid.Row className={classes.segmentDetailRow}>
        {financialTermItems.map((termItem, idx) => (
          <span key={`termItem_${idx}`} className={classes.spacer}>
            <span className={classes.textLabel}>
              {FinancialTermLabels[termItem.termType]}:
            </span>
            {termItem.termUnits === 'USD' ? '$' : ''}
            {termItem.termValue}
            {termItem.termUnits === 'PERCENTAGE' ? '%' : ''}
          </span>
        ))}
      </Grid.Row>
    </>
  );
};

const renderSegmentAction = (
  offerUuid: string,
  merchantId: string,
  segment: Segment,
  cancelAction: () => void,
  action: SegmentActionType | null,
  attachedAudiences: AttachedAudiences,
) => {
  switch (action) {
    case SegmentActionType.PULSE:
      return (
        <PulseSegment
          offerUuid={offerUuid}
          merchantId={merchantId}
          segment={segment}
          cancelUpdate={cancelAction}
          attachedAudiences={attachedAudiences}
        />
      );
    case SegmentActionType.REPLACE:
      return (
        <ReplaceSegment
          offerUuid={offerUuid}
          merchantId={merchantId}
          segment={segment}
          cancelUpdate={cancelAction}
          attachedAudiences={attachedAudiences}
        />
      );
    case SegmentActionType.TERMINATE:
      return (
        <TerminateSegment
          offerUuid={offerUuid}
          segment={segment}
          cancelUpdate={cancelAction}
        />
      );
    case SegmentActionType.MODIFY:
      return (
        <ModifySegment
          segment={segment}
          cancelUpdate={cancelAction}
          offerUuid={offerUuid}
        />
      );
    default:
      break;
  }

  return <em>Error: Unsupported Segment Action Type : {action}</em>;
};

type MatchParams = {
  merchantId: string;
  merchantName: string;
  offerUuid: string;
};

const OfferSegment: FunctionComponent<Props> = ({
  segment,
  disablePulse,
  activeSegment,
  setActiveSegment,
  attachedAudiences,
  expired,
  deleted,
  classes,
  offer: offerGetByUuid,
}) => {
  const [updateThrottle, setUpdateThrottle] = useState<boolean>(false);
  const [
    isUpdateNameAndDescriptionFormOpen,
    setIsUpdateNameAndDescriptionFormOpen,
  ] = useState<boolean>(false);
  const { merchantId = '', offerUuid = '' } = useParams<MatchParams>();

  const actionClickHandler = (action: SegmentActionType) => (
    e: SyntheticEvent<HTMLButtonElement, Event>,
  ) => {
    setActiveSegment({
      segmentUuid: (e.target as HTMLButtonElement).value,
      action,
    });
  };

  const handleCancelAction = () => {
    setActiveSegment({
      segmentUuid: null,
      action: null,
    });
  };

  return (
    <>
      {segment.uuid === activeSegment.segmentUuid ? (
        renderSegmentAction(
          offerUuid,
          merchantId,
          segment,
          handleCancelAction,
          activeSegment.action,
          attachedAudiences,
        )
      ) : (
        <Grid key={`segment_${segment.uuid}`} className={classes.segmentGrid}>
          {renderCashbackDetails(segment, classes, offerGetByUuid)}
          {renderFinancialTerms(segment, classes)}
          {renderTargetingRules(
            offerUuid,
            segment,
            updateThrottle,
            setUpdateThrottle,
            isUpdateNameAndDescriptionFormOpen,
            setIsUpdateNameAndDescriptionFormOpen,
            classes,
            expired,
          )}
          {!expired && !deleted && (
            <Grid.Row>
              <Grid.Column width="16">
                <Grid.Row data-tip data-for="manual-pulse">
                  {disablePulse && (
                    <ReactTooltip id="manual-pulse">
                      This pulse must be done manually
                    </ReactTooltip>
                  )}
                  <Button
                    disabled={disablePulse}
                    title="Pulse Segment"
                    onClick={actionClickHandler(SegmentActionType.PULSE)}
                    value={segment.uuid}
                  />
                  <Button
                    className={classes.button}
                    title="Replace Segment"
                    onClick={actionClickHandler(SegmentActionType.REPLACE)}
                    value={segment.uuid}
                  />
                  {(!segment.endDate ||
                    (segment.endDate &&
                      dateIsAfterCurrentDate(segment.endDate))) && (
                    <Button
                      className={classnames(
                        classes.button,
                        classes.terminateBtn,
                      )}
                      title="Terminate Segment"
                      onClick={actionClickHandler(SegmentActionType.TERMINATE)}
                      value={segment.uuid}
                    />
                  )}
                  <Button
                    className={classnames(classes.button, classes.modifyButton)}
                    title="Modify Segment"
                    onClick={actionClickHandler(SegmentActionType.MODIFY)}
                    value={segment.uuid}
                  />
                </Grid.Row>
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      )}
    </>
  );
};

export default withStyles(styles)(OfferSegment);
